import { useMemo } from 'react';

import { TabOption } from '@/components/chariot-ui';
import {
  ASSET_DRAWER_TAB_LABELS,
  ASSET_DRAWER_TAB_OPTIONS,
  ASSET_DRAWER_TABS,
} from '@/sections/asset/constants';
import { applicationRollupKey } from '@/sections/asset/utils';
import { Asset } from '@/types';

/**
 * Hook for managing Asset Drawer tab configuration
 *
 * @param asset - Optional asset for dynamic tab generation based on asset type
 * @returns Array of tab options for the drawer
 *
 * @example
 * ```typescript
 * const tabs = useAssetTabs(asset);
 * // Returns: [{ label: 'Overview', value: '' }, ...]
 * ```
 */
export function useAssetTabs(asset?: Asset): TabOption[] {
  return useMemo(() => {
    // Use base tab configuration from constants
    const baseTabs = [...ASSET_DRAWER_TAB_OPTIONS];

    // Add the Application tab when this drawer is showing — or rolls up to —
    // an Application. `applicationRollupKey` returns the asset's own key when
    // the asset IS the :Application (the common resolve-up path, where the
    // re-fetched parent carries NO `group_key`) and the parent's key when a
    // member observation is opened directly. Keying off `group_key` alone
    // (as before) silently dropped the tab on the primary path.
    if (asset && applicationRollupKey(asset)) {
      // Insert right after Overview so it's the first context-bearing tab.
      const overviewIndex = baseTabs.findIndex(
        tab => tab.value === ASSET_DRAWER_TABS.OVERVIEW
      );
      baseTabs.splice(overviewIndex >= 0 ? overviewIndex + 1 : 0, 0, {
        label: ASSET_DRAWER_TAB_LABELS[ASSET_DRAWER_TABS.APPLICATION],
        value: ASSET_DRAWER_TABS.APPLICATION,
      });
    }

    // Add Affiliation tab only when asset has affiliation results
    const hasAffiliationData = asset?.history?.some(
      item => item.filePath && item.affiliationVerdict
    );

    if (hasAffiliationData) {
      // Insert before Notes tab (last position)
      const notesIndex = baseTabs.findIndex(
        tab => tab.value === ASSET_DRAWER_TABS.NOTES
      );
      baseTabs.splice(notesIndex >= 0 ? notesIndex : baseTabs.length, 0, {
        label: ASSET_DRAWER_TAB_LABELS[ASSET_DRAWER_TABS.AFFILIATION],
        value: ASSET_DRAWER_TABS.AFFILIATION,
      });
    }

    return baseTabs;
    // Depend on the whole asset: the gate now reads key/class/group_key (via
    // applicationRollupKey) plus history, and the React Compiler infers the
    // object as the dependency — listing a property subset trips
    // preserve-manual-memoization.
  }, [asset]);
}
